import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';

import styles from './KaiCoachEditor.module.css';

// One selectable course chapter. `uid` is the component UID the backend keys the
// datastore lookup on; `statusLabel` ("Draft"/"Scheduled"/"Live"/"Closed")
// tells the instructor at a glance that attaching a not-yet-visible chapter is
// allowed — its content only ever reaches the AI, never the student.
export interface ChapterOption {
  statusLabel?: string;
  title: string;
  uid: string;
}

interface ChapterContextPickerProps {
  chapters: ChapterOption[];
  description: string;
  // What was attached when the editor opened, or null when this thread did not
  // exist yet. A brand-new session/activity cannot have students mid-conversation,
  // so it never warns.
  initialUids: string[] | null;
  // True when students can already reach this thread, so a change now lands
  // mid-conversation for anyone who has started.
  isLive: boolean;
  onChange: (next: string[]) => void;
  selectedUids: string[];
}

function sameSelection(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every((uid) => set.has(uid));
}

export function ChapterContextPicker({
  chapters,
  description,
  initialUids,
  isLive,
  onChange,
  selectedUids,
}: ChapterContextPickerProps) {
  const [open, setOpen] = useState(false);
  // The dialog edits a DRAFT so Cancel really discards; only Save commits.
  const [draftUids, setDraftUids] = useState<string[]>(selectedUids);
  const [query, setQuery] = useState('');
  const wasOpen = useRef(false);
  const descriptionId = useId();

  // Re-seed the draft from the committed value each time the dialog opens (and
  // never while it is open, which would clobber what the instructor is doing).
  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraftUids(selectedUids);
      setQuery('');
    }
    wasOpen.current = open;
  }, [open, selectedUids]);

  const chapterByUid = useMemo(
    () => new Map(chapters.map((chapter) => [chapter.uid, chapter])),
    [chapters],
  );
  const draftSet = useMemo(() => new Set(draftUids), [draftUids]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle === ''
      ? chapters
      : chapters.filter((chapter) => chapter.title.toLowerCase().includes(needle));
  }, [chapters, query]);

  // Preserve the instructor's chosen order, and keep a chapter that no longer
  // exists visible so it can be removed rather than silently disappearing.
  const selectedChapters = selectedUids.map(
    (uid) => chapterByUid.get(uid) ?? { title: 'Removed chapter', uid },
  );

  const showLiveWarning =
    isLive && initialUids !== null && !sameSelection(initialUids, selectedUids);

  function toggleDraft(uid: string) {
    setDraftUids((current) =>
      current.includes(uid)
        ? current.filter((selected) => selected !== uid)
        : [...current, uid],
    );
  }

  return (
    <fieldset aria-describedby={descriptionId} className={styles.group}>
      <legend className={styles.groupLegend}>Course material</legend>
      <div className={styles.groupBody}>
        <p className={styles.help} id={descriptionId}>
          {description}
        </p>

        {selectedChapters.length === 0 ? (
          <p className={styles.empty}>No chapters attached.</p>
        ) : (
          <ul className={styles.pickerChips}>
            {selectedChapters.map((chapter) => (
              <li className={styles.pickerChip} key={chapter.uid}>
                <span>{chapter.title}</span>
                <button
                  aria-label={`Remove ${chapter.title}`}
                  onClick={() =>
                    onChange(selectedUids.filter((uid) => uid !== chapter.uid))
                  }
                  type="button"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div>
          <Button onClick={() => setOpen(true)} size="small" variant="secondary">
            {selectedUids.length > 0 ? 'Edit chapters' : 'Add chapters'}
          </Button>
        </div>

        {showLiveWarning ? (
          <p aria-live="polite" className={styles.liveWarning} role="status">
            This session is already open to students. Anyone who has already started
            working with Kai was coached without this material — it only applies from
            their next message onward.
          </p>
        ) : null}
      </div>

      <Dialog.Root onOpenChange={setOpen} open={open}>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.dialogOverlay} />
          <Dialog.Content className={styles.dialogContent}>
            <Dialog.Title className={styles.dialogTitle}>
              Select course material
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.help}>
                Kai reads these chapters as background. Students never see this
                content, so chapters that are not yet visible to them can be used.
              </p>
            </Dialog.Description>

            {chapters.length === 0 ? (
              <p className={styles.empty}>No other chapters in this course yet.</p>
            ) : (
              <>
                <input
                  aria-label="Search chapters"
                  className={styles.input}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search chapters…"
                  type="search"
                  value={query}
                />
                <p aria-live="polite" className={styles.pickerCount}>
                  {draftUids.length} of {chapters.length} chapters selected
                </p>
                <div className={styles.pickerOptions}>
                  {filtered.length === 0 ? (
                    <p className={styles.empty}>No chapters match “{query}”.</p>
                  ) : null}
                  {filtered.map((chapter) => (
                    <label className={styles.pickerOption} key={chapter.uid}>
                      <input
                        checked={draftSet.has(chapter.uid)}
                        onChange={() => toggleDraft(chapter.uid)}
                        type="checkbox"
                      />
                      <span>{chapter.title}</span>
                      {chapter.statusLabel ? (
                        <span className={styles.pickerStatus}>
                          {chapter.statusLabel}
                        </span>
                      ) : null}
                    </label>
                  ))}
                </div>
              </>
            )}

            <div className={styles.dialogActions}>
              <Dialog.Close asChild>
                <Button variant="secondary">Cancel</Button>
              </Dialog.Close>
              <Button
                onClick={() => {
                  onChange(draftUids);
                  setOpen(false);
                }}
              >
                Save chapters
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </fieldset>
  );
}
